import React, { useContext, useEffect, useState } from 'react';
import Select from 'react-select';
import { endpointContext } from '../contexts/endpointContext';
import { QCPageCards } from '../components/buttons/QCPageCards';
import { ExportStatusBadge, ExportTable } from '../components/tables/ExportTable';
import { EXPORT_COMMODITIES, EXPORT_WAITING_MESSAGE, getDisplayExportRows } from '../helpers/exportHelpers';
import useGainsQCApi from '../services/useGainsQCApi';
import useDataExport from '../services/useDataExport';

const ADDITIONAL_BUTTON_STYLE = {
	height: '130px',
	width: '280px',
	marginTop: '15px'
};

const STATUS_MESSAGES = {
	WAITING: EXPORT_WAITING_MESSAGE,
	RUNNING: 'Export in progress.',
	FINISHED: 'Export completed successfully.',
	PARTIAL: 'Export completed with some site failures. See the affected IDs below.',
	FAILED: 'Export failed. See the available details below.',
	UNAVAILABLE: 'No status available for the complete export. See dataset details below.'
};

const ExportControls = ({ commodity, exportType, onBusyChange }) => {
	const { dbData } = useContext(endpointContext);
	const [siteScope, setSiteScope] = useState('all');
	const [selectedSites, setSelectedSites] = useState([]);
	const fetchSites = useGainsQCApi();
	const { makeRequest } = fetchSites;
	const exportJob = useDataExport(commodity.value, exportType);
	const { starting, loading, pending, state, statusError } = exportJob;
	const selectedScope = exportType === 'site' && siteScope === 'selected';
	const commodityData = dbData?.allCommodities?.find((item) =>
		[item.name, item.commodity, item.symbol].some((name) =>
			name?.toLowerCase() === commodity.label.toLowerCase() || name === commodity.value
		)
	);
	const siteOptions = (fetchSites.data?.data || [])
		.filter((site) => commodityData && String(site.commodity_id) === String(commodityData.id))
		.map(({ id, name }) => ({ value: id, label: `${id}: ${name}` }));
	const validSelection = !selectedScope || (selectedSites.length > 0 &&
		!fetchSites.loading && !fetchSites.error &&
		selectedSites.every((site) => siteOptions.some((option) => option.value === site.value)));
	const busy = starting || (Boolean(pending) && state !== 'FAILED') || state === 'RUNNING';

	useEffect(() => {
		onBusyChange(starting);
		return () => onBusyChange(false);
	}, [starting, onBusyChange]);

	useEffect(() => {
		if (selectedScope) makeRequest('/smelters/sites', 'GET');
	}, [selectedScope, makeRequest]);

	return (
		<>
			{exportType === 'site' ? (
				<div className="mt-4">
					<h5>Site scope</h5>
					{[['all', 'All sites'], ['selected', 'Selected sites']].map(([value, label]) => (
						<div className="form-check form-check-inline" key={value}>
							<input className="form-check-input" type="radio" name="export-site-scope"
								id={`export-scope-${value}`} checked={siteScope === value}
								onChange={() => setSiteScope(value)} disabled={busy} />
							<label className="form-check-label ms-2" htmlFor={`export-scope-${value}`}>{label}</label>
						</div>
					))}
					{selectedScope && (
						<div className="mt-3" style={{ maxWidth: '700px' }}>
							<label htmlFor="export-site-ids" className="form-label">Site IDs</label>
							<Select inputId="export-site-ids" isMulti isSearchable
								options={siteOptions} value={selectedSites} onChange={setSelectedSites}
								isLoading={fetchSites.loading} isDisabled={busy || fetchSites.loading || !commodityData}
								placeholder={`Search ${commodity.label.toLowerCase()} sites...`} />
							{!commodityData && <p className="mt-2">Site selection is unavailable until commodity information has loaded.</p>}
							{fetchSites.error && (
								<div className="alert alert-danger mt-2" role="alert">
									Could not load sites. {fetchSites.error.message}{' '}
									<button className="btn btn-outline-secondary btn-sm" disabled={fetchSites.loading}
										onClick={() => makeRequest('/smelters/sites', 'GET')}>Retry</button>
								</div>
							)}
						</div>
					)}
				</div>
			) : (
				<p className="mt-4">Export all aggregate data for {commodity.label.toLowerCase()} together.</p>
			)}

			<div className="row mt-3">
				<div className="col-auto">
					<QCPageCards
						onClickFunction={() => exportJob.startExport(selectedScope
							? { ids: selectedSites.map((site) => site.value) } : {})}
						cardTitle={starting ? 'Requesting export...' : `Start ${exportType === 'site' ? 'site' : 'aggregate'} export`}
						cardText={`${commodity.label} — ${selectedScope ? `${selectedSites.length} selected sites` : exportType === 'site' ? 'all sites' : 'all aggregate data'}`}
						cardIcon={<i className="fa-solid fa-file-export ps-3"></i>}
						additionalStyle={ADDITIONAL_BUTTON_STYLE}
						disabled={busy || loading || !validSelection || Boolean(statusError)}
					/>
				</div>
			</div>
			{exportJob.startError && <div className="alert alert-danger mt-3" role="alert">{exportJob.startError}</div>}

			<div className="mt-5">
				<div className="d-flex flex-wrap align-items-center gap-3">
					<h4 className="mb-0">Latest export status</h4>
					<button className="btn btn-outline-secondary" onClick={exportJob.refreshStatus}
						disabled={loading || starting}>
						{loading ? 'Refreshing...' : 'Refresh status'}
					</button>
				</div>
				{statusError && (
					<div className="alert alert-warning mt-3" role="alert">
						Could not refresh status. {statusError} Last known results may be outdated.
						Automatic refresh is paused; use Refresh status to retry.
					</div>
				)}
				<div className="mt-3" role="status" aria-live="polite">
					{['RUNNING', 'FINISHED', 'PARTIAL', 'FAILED'].includes(state) && (
						<><ExportStatusBadge state={state} />{' '}</>
					)}
					{STATUS_MESSAGES[state]}
				</div>
				{exportJob.updatedAt && <p className="text-muted mt-2">Last checked: {exportJob.updatedAt}</p>}
				{(pending || state === 'RUNNING') && state !== 'FAILED' && !statusError && (
					<p className="text-muted">Status refreshes every {exportJob.refreshInterval === 15000 ? '15 seconds' : '1 minute'}.</p>
				)}
				{pending && <p>Previous export results are hidden until updated status is available.</p>}
				<ExportTable rows={getDisplayExportRows(exportJob.rows, Boolean(pending))} waiting={Boolean(pending)} />
			</div>
		</>
	);
};

export const DataExports = () => {
	const [commodity, setCommodity] = useState(EXPORT_COMMODITIES[0]);
	const [exportType, setExportType] = useState('site');
	const [starting, setStarting] = useState(false);

	return (
		<div className="col px-0">
			<div className="contentContainer">
				<hr style={{ marginTop: '58px' }}></hr>
				<h2>Data exports</h2>
				<h5>Export site or aggregate data for a commodity</h5>
				<div className="mt-4" style={{ maxWidth: '350px' }}>
					<label htmlFor="export-commodity" className="form-label">Commodity</label>
					<Select inputId="export-commodity" options={EXPORT_COMMODITIES}
						value={commodity} onChange={setCommodity} isDisabled={starting} isSearchable={false} />
				</div>
				<div className="nav nav-tabs mt-4" role="tablist" aria-label="Export type">
					{[['site', 'Site data'], ['agg', 'Aggregate data']].map(([value, label]) => (
						<button key={value} className={`nav-link ${exportType === value ? 'active' : ''}`}
							id={`export-tab-${value}`} role="tab" aria-selected={exportType === value}
							aria-controls="export-panel" onClick={() => setExportType(value)} disabled={starting}>
							{label}
						</button>
					))}
				</div>
				<div id="export-panel" role="tabpanel" aria-labelledby={`export-tab-${exportType}`}>
					<ExportControls key={`${commodity.value}:${exportType}`} commodity={commodity}
						exportType={exportType} onBusyChange={setStarting} />
				</div>
			</div>
		</div>
	);
};
